import { useReducer, useCallback } from 'react';

const useTestUpdate = () => {
  const initialState = {
    dotsForDisplay: [],
    seenDots: [],
    missedOnceDots: [],
    missedTwiceDots: [],
    logmarSize: 1.0,
    finalLogmarSize: null,
    failedLogmarSizes: [],
    blindSpotCount: 0,
    blindSpotSeenCount: 0,
    repeatedSeenDotCount: 5,
    activeDot: null,
    orientationCorrect: [],
    orientationIncorrect: [],
    currentLandoltCQuadrant: null,
    patientQuadrantResponse: null,
    testEnded: false,
    testConfidence: 100,
  };

  const initialiseAllValues = () => {
    return initialState;
  };

  const forthValleyReducer = (existingData, action) => {
    const {
      dotsForDisplay,
      seenDots,
      logmarSize,
      missedOnceDots,
      missedTwiceDots,
      blindSpotCount,
      blindSpotSeenCount,
      repeatedSeenDotCount,
      orientationCorrect,
      orientationIncorrect,
      failedLogmarSizes,
      testConfidence,
    } = existingData;

    switch (action.type) {
      case 'updateLogmarSize':
        let logmarSizeCanUpdate = true;
        if (logmarSize === 1 && action.payload === 0.1) {
          logmarSizeCanUpdate = false;
        } else if (logmarSize.toFixed(1) === '0.1' && action.payload === -0.1) {
          logmarSizeCanUpdate = false;
        }
        return { ...existingData, logmarSize: logmarSizeCanUpdate ? logmarSize + action.payload : logmarSize };
      case 'setFinalLogmarSize':
        return { ...existingData, logmarSize: action.payload, finalLogmarSize: action.payload };
      case 'updateFailedLogmarSizes':
        return { ...existingData, failedLogmarSizes: [...failedLogmarSizes, action.payload] };
      case 'addSeenDot':
        return { ...existingData, seenDots: [...seenDots, action.payload] };
      case 'addMissedOnceDot':
        return { ...existingData, missedOnceDots: [...missedOnceDots, action.payload] };
      case 'addMissedTwiceDot':
        return { ...existingData, missedTwiceDots: [...missedTwiceDots, action.payload] };
      case 'setDotsForDisplay':
        return { ...existingData, dotsForDisplay: action.payload };
      case 'addDotForDisplay':
        return { ...existingData, dotsForDisplay: [...dotsForDisplay, action.payload] };
      case 'removeFromMissedOnceArray':
        const newMissedOnceArray = [...missedOnceDots];
        newMissedOnceArray.splice(action.payload, 1);
        return { ...existingData, missedOnceDots: [...newMissedOnceArray] };
      case 'removeFromSeenDotArray':
        const newSeenDotArray = [...seenDots];
        const indexOfDotToRemove = newSeenDotArray.findIndex((dot) => {
          return dot?.id === action.payload.id;
        });
        newSeenDotArray.splice(indexOfDotToRemove, 1);
        return { ...existingData, seenDots: [...newSeenDotArray] };
      case 'incrementBlindSpotCount':
        return { ...existingData, blindSpotCount: blindSpotCount + 1 };
      case 'incrementBlindSpotSeenCount':
        return { ...existingData, blindSpotSeenCount: blindSpotSeenCount + 1 };
      case 'decrementRepeatedSeenDotCount':
        return { ...existingData, repeatedSeenDotCount: repeatedSeenDotCount - 1 };
      case 'decreaseTestConfidence':
        if (testConfidence > 0) {
          return { ...existingData, testConfidence: testConfidence - 10 };
        } else return { ...existingData, testEnded: true };
      case 'updateActiveDot':
        return { ...existingData, activeDot: action.payload };
      case 'updateOrientationCorrect':
        return { ...existingData, orientationCorrect: [...orientationCorrect, action.payload] };
      case 'updateOrientationIncorrect':
        return { ...existingData, orientationIncorrect: [...orientationIncorrect, action.payload] };
      case 'updateCurrentLandoltCQuadrant':
        return { ...existingData, currentLandoltCQuadrant: action.payload };
      case 'updatePatientQuadrantResponse':
        return { ...existingData, patientQuadrantResponse: action.payload };
      case 'setTestEnded':
        return { ...existingData, testEnded: action.payload };
      case 'reset':
        return initialiseAllValues();
      default:
        return existingData;
    }
  };

  const [testProgressData, updateTestProgressData] = useReducer(forthValleyReducer, initialState, initialiseAllValues);

  const batchMultipleUpdates = useCallback((updates) => {
    for (const type in updates) {
      updateTestProgressData({ type: type, payload: updates[type] });
    }
  }, []);

  return { testProgressData, updateTestProgressData, batchMultipleUpdates };
};

export default useTestUpdate;
